import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {LEGACY_FONT_PROXIMA_12} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {
  BLUE,
  BLUE_LIGHT,
  BLUE_POWDER,
  GRAY_DARKER,
  GRAY_LIGHT,
  GREEN,
  GREEN_LIGHTER,
  GREEN_DARKER,
  PURPLE_DARK,
  PURPLE_LIGHTER,
  RED,
  RED_POWDER,
  YELLOW_DARKER,
  YELLOW_LIGHTER,
} from 'ui-library/Colors';

import BenchlingIcon, {BenchlingIconName} from './icons/BenchlingIcon';

export enum BadgeColor {
  BLUE = 'blue',
  COLLECTIBLE_BLUE = 'collectible-blue',
  GRAY = 'gray',
  GREEN = 'green',
  GREEN_FILLED = 'green-filled',
  RED = 'red',
  PURPLE = 'purple',
  YELLOW = 'yellow',
}
interface Props {
  className?: string;
  children: ReactNode;
  color: BadgeColor;
  bold?: boolean;
  startingIcon?: BenchlingIconName;
  dataTestAttrs?: DataTestAttrs;
}

export default function Badge(props: Props): JSX.Element {
  return (
    <div
      className={classNames(
        props.className,
        css(styles.badge),
        css(colorStyleMap[props.color]),
        props.bold && css(styles.bold)
      )}
      {...getTestProps(props.dataTestAttrs)}
    >
      {props.startingIcon && <BenchlingIcon icon={props.startingIcon} style={{marginRight: 4}} />}
      {props.children}
    </div>
  );
}

const styles = StyleSheet.create({
  blue: {
    backgroundColor: BLUE_POWDER,
    color: BLUE_LIGHT,
  },
  collectibleBlue: {
    backgroundColor: Color.BLUE_150,
    color: BLUE,
  },
  red: {
    backgroundColor: RED_POWDER,
    color: RED,
  },
  gray: {
    backgroundColor: GRAY_LIGHT,
    color: GRAY_DARKER,
  },
  green: {
    backgroundColor: GRAY_LIGHT,
    color: GREEN,
  },
  greenFilled: {
    backgroundColor: GREEN_LIGHTER,
    color: GREEN_DARKER,
  },
  purple: {
    backgroundColor: PURPLE_LIGHTER,
    color: PURPLE_DARK,
  },
  yellow: {
    backgroundColor: YELLOW_LIGHTER,
    color: YELLOW_DARKER,
  },
  badge: {
    ...LEGACY_FONT_PROXIMA_12,
    borderRadius: 4,
    display: 'inline-block',
    padding: 6,
    textTransform: 'uppercase',
  },
  bold: {
    fontWeight: 'bold',
  },
});

const colorStyleMap: Partial<Record<BadgeColor, object>> = {
  [BadgeColor.BLUE]: styles.blue,
  [BadgeColor.COLLECTIBLE_BLUE]: styles.collectibleBlue,
  [BadgeColor.GRAY]: styles.gray,
  [BadgeColor.GREEN]: styles.green,
  [BadgeColor.GREEN_FILLED]: styles.greenFilled,
  [BadgeColor.RED]: styles.red,
  [BadgeColor.PURPLE]: styles.purple,
  [BadgeColor.YELLOW]: styles.yellow,
};
